import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../libraries/tailwind/utils';
import Icon from '../renders/icon/Icon';
import Entity from '../renders/entity/Entity';
import PropTypes from 'prop-types';
import createDataCyAttribute from '@/common-utils/functions/createDataCyAttribute';

const buttonVariants = cva(
  // eslint-disable-next-line max-len
  'inline-flex  items-center justify-center whitespace-nowrap rounded-md text-base font-medium ring-offset-background transition-colors focus-visible:outline-none  disabled:cursor-not-allowed disabled:opacity-50 ',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground border bg-white',
        ghostNoBorder: 'hover:bg-accent hover:text-accent-foreground bg-transparent',
        topbar: 'bg-transparent text-white border border-white rounde select-none',
        pageDropdown: 'bg-transparent text-black border border-gray rounde select-none',
        success: 'bg-success text-white hover:bg-success/80',
        'success-outline': 'border border-success text-success-foreground hover:bg-success hover:text-white focus:ring-success-600',
        // danger: 'bg-danger text-white hover:bg-danger/80',
        'danger-outline': 'border border-danger text-danger-foreground hover:bg-danger hover:text-white focus:ring-danger-600',
        warning: 'bg-warning text-white hover:bg-warning/80',
        'warning-outline': 'border border-warning text-warning-foreground hover:bg-warning hover:text-white focus:ring-warning-600',
        info: 'bg-info text-white hover:bg-info/80',
        'info-outline': 'border border-info text-info-foreground hover:bg-info hover:text-white focus:ring-info-600',
        blue500: 'bg-blue500 text-white hover:bg-blue500/80',
        'blue500-outline': 'border border-blue500 text-blue500-foreground hover:bg-blue500 hover:text-white focus:ring-blue500-600',
        'secondary-outline': 'border border-gray-500 text-gray-500 hover:bg-gray-500 hover:text-white focus:ring-secondary-600',
        // New variants
        link: 'text-primary w-fit p-0',
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-primary-disabled',
        secondary:
          'bg-secondary text-secondary-foreground border border-secondary-foreground hover:bg-secondary-hover disabled:text-secondary-disabled',
        critical:
          'bg-secondary text-secondary-foreground border border-secondary-foreground hover:text-danger hover:border-danger disabled:text-secondary-disabled',
        danger:
          'bg-white text-danger border border-danger hover:bg-danger/10'
      },
      size: {
        default: 'h-8 rounded-md px-3 py-0.5',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
        auto: 'h-8 rounded-md px-3 py-0.5 sm:w-auto'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

const Button = React.forwardRef(({ className, variant, size, namespace = '', entity, asChild = false, icon = false,
  children = false, iconClassName = null, outline = false, hideOnMedium = false, dataCy = '', ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  const hideText = hideOnMedium && 'hidden lg:block';
  const textRender = entity && <Entity namespace={namespace} entity={entity} className={cn(`text-inherit ${hideText}`)} />;
  const iconRender = icon ? <Icon icon={icon} className={textRender ? cn('mr-1', iconClassName) : iconClassName} /> : null;
  const childrenRender = children || <>{iconRender} {textRender}</>;
  size = icon && !textRender && !size ? 'icon' : size;
  variant = outline ? `${variant}-outline` : variant;
  dataCy = dataCy ? createDataCyAttribute(dataCy) : createDataCyAttribute(entity || icon, 'btn-');

  return (
    (<Comp
      data-cy={dataCy}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}>
      {childrenRender}
    </Comp>
    )
  );
});
Button.displayName = 'Button';

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  namespace: PropTypes.string,
  entity: PropTypes.string,
  asChild: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  children: PropTypes.node,
  iconClassName: PropTypes.string,
  outline: PropTypes.bool,
  hideOnMedium: PropTypes.bool,
  dataCy: PropTypes.string
};

export { Button, buttonVariants };
